import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Router} from '@angular/router';
import {environment} from '../../environments/environment';
import {BehaviorSubject, Observable, timer, EMPTY} from "rxjs";
import { StorageService } from './storage.service';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { isSubscriptionActive } from './admin-account-status.util';

export interface KilnCatalogRow {
  id: string;
  name: string;
  role: string;
  account_id: number;
  account_name?: string;
  account_type?: string | null;
  account_active?: boolean | null;
  account_subscription_end_date?: string | null;
}

@Injectable({ providedIn: 'root' })
export class UserService {
  private claimUrl = environment.claimApi;
  private controllerUrl = environment.kilnApi;
  public userLoginData;
  public loggedInAsUserLoginData;
  public allKilnInfo: KilnCatalogRow[] = [];
  private allKilnInfoOwner: string | null = null;
  private loadAllKilnInfoRequestId = 0;
  public accountOptionsForFilter: Array<{ account_id: number; account_name?: string }> = [];
  public accountFilterOptionsUpdate = new BehaviorSubject<void>(undefined);
  public accountKilnFilter: 'all' | number = 'all';
  public isLoggenIn = false;
  private updateUserInfoTimer: any;
  public updateTime = 100;
  private loginAs;
  public loginAsInformation = new BehaviorSubject<any>({});
  public loginStatus = new BehaviorSubject<any>({});
  public storageUpdate = new BehaviorSubject<any>({});
  public currentAccountUpdate = new BehaviorSubject<any>({})
  private redirect;
  private loginInFlight = false;

  constructor(
    public http: HttpClient,
    private storageService: StorageService,
    private router: Router,
    private translate: TranslateService
    ) {
    this.startTimerSecond();
  }

  private applyPreferredLanguageFromLogin(response: any) {
    const preferredLanguage = response?.preferredLanguage;
    const allowedLanguages = ['en', 'fr', 'de', 'es'];
    if (!preferredLanguage || !allowedLanguages.includes(preferredLanguage)) {
      return;
    }
    this.translate.use(preferredLanguage);
    this.storageService.set('preferredLanguage', preferredLanguage);
  }

  public startTimerSecond() { // This timer is used to flash messages and as the time base for the update timer
    if(!this.updateUserInfoTimer) {
      let TimerSecond = timer(10000, 100000);
      this.updateUserInfoTimer = TimerSecond.subscribe(t => this.onTickTimerSecond(t));
    }
  }

  public killTimerSecond() {
    if(this.updateUserInfoTimer) {
      this.updateUserInfoTimer.unsubscribe();
    }
  }

  public onTickTimerSecond(tick) {
    if(this.LoggedIn()) {
      this.updateTime--;
      if (this.updateTime <= 0) {
        this.updateLoginData()
      }
    }
  }

  public setTimerSecond(seconds) { this.updateTime = seconds; }

  public addKilnSuccess(data) {
    var id = data.serial_number ? data.serial_number : data.external_id;
    var already_has_kiln = this.userLoginData.kiln_info.some((kiln) => { return kiln.id == id});
    if(!already_has_kiln) {
      this.userLoginData.kiln_info.push({id: id, name: data.name});
      // console.log(this.userLoginData.kiln_info);
    }
  }

  public unclaim(kilnId, unclaim_for_user) {
    if(!unclaim_for_user || this.userLoginData.email == unclaim_for_user) {
      this.userLoginData.kiln_info = this.userLoginData.kiln_info.filter((kiln) => { return kiln.id != kilnId});
    }
  }

  public getStoredEmail() {
    this.storageService.get('email').then((val) => {
      this.storageUpdate.next({...this.storageUpdate.getValue(), ...{email: val}});
    });
  }

  public getStoredPassword() {
    this.storageService.get('password').then((val) => {
      this.storageUpdate.next({...this.storageUpdate.getValue(), ...{password: val}});
    });
  }

  public setStoredUserData(email, password) {
    this.storageService.set('email', email);
    this.storageService.set('password', password);
  }

  public removeStoredUserData() {
    this.storageService.remove('email');
    this.storageService.remove('password');
    this.isLoggenIn = false;
  }

  setLoginAs(user, account_id): Promise<any> {
    this.loginAs = user;
    this.accountKilnFilter = 'all';
    this.clearAccountCatalogCache();
    return this.getLoginAsInformation(user.email, account_id, user.username);
  }

  unsetLoginAs() {
    this.loginAs = undefined;
    this.loggedInAsUserLoginData = undefined;
    if(this.userLoginData) {
      this.userLoginData.loggedInAs = undefined;
      delete this.userLoginData.loggedInAs;
    }

    this.accountKilnFilter = 'all';
    this.clearAccountCatalogCache();
    this.loginAsInformation.next(this.loggedInAsUserLoginData);
    void this.refreshAccountFilterOptions();
  }

  public getLoginAsInformation(email: string, account_id?, username?): Promise<any> {
    var loginUrl = this.claimUrl + "/premium/admin/login";
    var data = {email: email && email.trim()}
    if(!email && username) {
      data["username"] = username
    }
    if(account_id && account_id !== '0') { data["account_id"] = account_id }

    return new Promise((resolve, reject) => {
      let loginAsPromise = this.http.post(loginUrl, data, this.headersWithToken())
      loginAsPromise.subscribe(
        res => {
          if(this.loginAs) {
            this.loggedInAsUserLoginData = {...this.loggedInAsUserLoginData, ...res};
            this.loggedInAsUserLoginData.email = email
            if(username) { this.loggedInAsUserLoginData.username = username }
            this.loginAsInformation.next(this.loggedInAsUserLoginData)
            Promise.all([
              this.refreshAccountFilterOptions(),
              this.loadAllKilnInfo(),
            ]).finally(() => resolve(this.loggedInAsUserLoginData));
            return;
          }

          resolve(this.loggedInAsUserLoginData);
        });
    })
  }

  public getUsersAsAdmin(emailSearch?: string): Promise<any> {
    var loginUrl = this.claimUrl + "/premium/admin/users";
    if (emailSearch) {
      loginUrl += `?email=${emailSearch}`;
    }
    return this.http.get(loginUrl, this.headersWithToken()).toPromise();
  }

  public adminConfirmUser(email): Promise<any> {
    var confirmationUrl = `${this.claimUrl}/admin/user/confirm`;
    const httpOptions = { headers: this.genericHeaders() }
    let data = {
      email: email
    };

    return this.http.post(confirmationUrl, data, httpOptions).toPromise()
  }

  public searchUser(email) {
    var loginUrl = this.claimUrl + "/admin/user/search";
    return this.http.post(loginUrl, {value: email}, this.headersWithToken()).toPromise();
  }

  public adminGetUser(email) {
    var loginUrl = this.claimUrl + "/admin/user";
    return this.http.post(loginUrl, {email: email}, this.headersWithToken()).toPromise();
  }

  public setAdminPassword(email: string, password: string, permanent = false): Promise<any> {
    var loginUrl = this.claimUrl + "/admin/user/set";
    return this.http.post(loginUrl, {
      email: email,
      temporaryPassword: password,
      permanent: permanent,
    }, this.headersWithToken()).toPromise();
  }

  /** @deprecated Use setAdminPassword */
  public setTemporaryPassword(email, password) {
    return this.setAdminPassword(email, password, false);
  }

  public adminResetPassword(email) {
    var loginUrl = this.claimUrl + "/admin/user/password";
    return this.http.post(loginUrl, {email: email}, {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'x-access-token': this.userLoginData.token
      })
    }).toPromise();
  }

  public adminDeleteUser(email) {
    var loginUrl = this.claimUrl + "/admin/user/delete";
    return this.http.post(loginUrl, {email: email}, this.headersWithToken()).toPromise();
  }

  public setRedirect(redirect, fragment) {
    this.redirect = {path: redirect, fragment: fragment};
  }
  public clearRedirect() { this.redirect = null;} 
  public getRedirect() {return this.redirect; }

  private pendingNewPassword: {
    email: string;
    old_password: string;
    userAttributes?: Record<string, string>;
  } | null = null;

  public setPendingNewPassword(data: {
    email: string;
    old_password: string;
    userAttributes?: Record<string, string>;
  }): void {
    this.pendingNewPassword = data;
  }

  public getPendingNewPassword() {
    return this.pendingNewPassword;
  }

  public clearPendingNewPassword(): void {
    this.pendingNewPassword = null;
  }

  public isLoginInFlight(): boolean {
    return this.loginInFlight;
  }

  public tryLogin(email: string, password: string, username: string = null): Observable<any> {
    if (email === password) {
      return EMPTY;
    }
    if (this.loginInFlight) {
      return EMPTY;
    }

    var loginUrl = this.claimUrl + "/users/login";
    let data = {
     "email": email && email.trim(),
     "password": password
    }

    if(!email && username) { data["username"] = username }
    const httpOptions = {
      headers: this.genericHeaders()
    }

    return new Observable((observer) => {
      this.loginInFlight = true;
      this.http.post(loginUrl, data, httpOptions).subscribe({
        next: (res: any) => {
          this.loginInFlight = false;
          this.userLoginData = {...this.userLoginData, ...res};
          this.applyPreferredLanguageFromLogin(res);
          if (email) {
            this.userLoginData.email = email;
          }

          if (res.newPasswordRequired) {
            this.isLoggenIn = false;
            observer.next({
              ...this.userLoginData,
              newPasswordRequired: true,
              userAttributes: this.sanitizeNewPasswordUserAttributes(res.attributes),
            });
            observer.complete();
            return;
          }

          if (res.token) {
            this.isLoggenIn = true;
            let return_response = this.userLoginData;
            if (this.loginAs) {
              return_response = {...return_response, ...{loggedInAs: this.loggedInAsUserLoginData.email}};
            }
            if (res.needs_terms_accepted) {
              return_response = {...return_response, ...{needsToAgreeToTermsAgain: true}};
            }
            observer.next(return_response);
            observer.complete();
            return;
          }

          this.isLoggenIn = false;
          observer.error({ error: { message: res.message || 'Login failed' } });
        },
        error: (err) => {
          this.loginInFlight = false;
          observer.error(err);
        },
      });
    });
  }

  public updateLoginData(): Promise<any> {
    if (this.isLoggedInAsAnotherUser()) {
      const accountId = this.loggedInAsUserLoginData?.account;
      if (accountId != null && accountId !== '' && accountId !== 0 && accountId !== '0') {
        return this.setCurrentAccount(accountId);
      }
      return Promise.resolve(this.loggedInAsUserLoginData);
    }

    if(this.userLoginData && this.userLoginData.account) {
      return this.setCurrentAccount(this.userLoginData.account);
    } else {
      return new Promise((resolve, reject) => {
        var loginUrl = this.claimUrl + "/users";
        let updateLoginPromise = this.http.get(loginUrl, this.headersWithToken())
        updateLoginPromise.subscribe(res => {
          this.userLoginData = {...this.userLoginData, ...res};
          resolve(this.userLoginData)
        });
      })
    }
  }

  public tryLogOut(token = null): Promise<any> {
    this.unsetLoginAs();
    var logoutUrl = this.claimUrl + "/users/logout";
    var data = {
      "x-access-token": token || (this.userLoginData && this.userLoginData.token)
    }

    const httpOptions = {
      headers: this.genericHeaders()
    }

    this.isLoggenIn = false;
    this.storageService.remove('rememberMe');
    return this.http.post(logoutUrl, data, httpOptions).toPromise()
      .then((response) => {
        this.router.navigate(['/home']);
        return response;
      })
      .catch((err) => {
        this.router.navigate(['/home']);
        throw err;
      });
  }

  public saveToken(token): Promise<any> {
    if(this.isLoggenIn) {
      var loginUrl = this.claimUrl + `/users/token.json?user_email=${this.userLoginData.email}&user_token=${this.userLoginData.token}`;
      var data = {
        "push_token": {
          token: token
        }
      }

      const httpOptions = {
        headers: this.genericHeaders()
      }
      return this.http.post(loginUrl, data, httpOptions).toPromise()
    }
  }

  public acceptTerms(email: string, token: string): Promise<any> {
    var acceptUrl = this.claimUrl + "/users/terms/accept";

    const httpOptions = this.headersWithToken();

    this.userLoginData.needs_to_accept_new_terms_and_conditions = false;
    return this.http.get(acceptUrl, httpOptions).toPromise();
  }

  public getLatestTerms(): Promise<any> {
    const termsUrl = this.claimUrl + "/terms-and-conditions";
    return this.http.get<any>(termsUrl, this.headersWithToken()).toPromise();
  }

  public hasElevatedPermissions() {
    if(this.userLoginData) {
      return ["super-admin", "tech"].includes(this.userLoginData.role)
    } else {
      return false;
    }
  }

  public isSkuttAdmin(): boolean {
    return this.userLoginData?.role === 'skutt-admin';
  }

  public accountExpired() {
    const loginData = this.isLoggedInAsAnotherUser()
      ? (this.loggedInAsUserLoginData || this.userLoginData?.loggedInAs)
      : this.userLoginData;

    if (!loginData || loginData.premium === true) {
      return false;
    }

    const accountId = loginData.account;
    const hasAccount = accountId !== undefined && accountId !== 0 && accountId !== '0';
    if (!hasAccount) {
      return false;
    }

    const subscriptionEnd = loginData.subscription_end_date;
    if (subscriptionEnd == null || subscriptionEnd === '') {
      return false;
    }

    return moment().isAfter(moment(subscriptionEnd));
  }

  public shouldShowPremiumUi(): boolean {
    return environment.show_premium && (this.isPremium() || this.accountExpired());
  }

  public setInPremiumTrial() {
    this.userLoginData.role = "premium-user"
    this.userLoginData.premium = true;
  }

  public isPremium() {
    if(this.userLoginData && !this.isLoggedInAsAnotherUser()) {
      return ["super-admin", "tech"].includes(this.userLoginData.role) || ("premium-user" === this.userLoginData.role && this.userLoginData.premium);
    } else if(this.isLoggedInAsAnotherUser()) {
      let loginAsVal = this.loginAsInformation.getValue()
      return loginAsVal && loginAsVal.premium;
    } else {
      return false;
    }
  }

  /** If the email exists in Cognito, send a KilnLink sign-in reminder to that inbox. */
  public recoverLoginEmail(email: string): Promise<{ success: boolean; message: string }> {
    const url = `${this.claimUrl}/users/email/recover`;
    return this.http
      .post<{ success: boolean; message: string }>(
        url,
        { email: email.trim().toLowerCase() },
        {
          headers: new HttpHeaders({
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }),
        }
      )
      .toPromise();
  }

  public createForgotPassword(username: string): Promise<any> {
    var newUserUrl = this.claimUrl + '/users/password/forgot';
    const httpOptions = {
      headers: this.genericHeaders()
    }

    return this.http.post(newUserUrl, {email: username.trim() }, httpOptions).toPromise()
  }

  public createNewUser(user): Promise<any> {
    var newUserUrl = `${this.claimUrl}/users/`;
    const httpOptions = {
      headers: this.genericHeaders()
    }
    let data: any = {
      email: user.email && user.email.trim(),
      password: user.password,
      password_confirmation: user.password
    };
    if (user.first_name != null && user.first_name !== '') {
      data.first_name = user.first_name.trim();
    }
    if (user.last_name != null && user.last_name !== '') {
      data.last_name = user.last_name.trim();
    }
    if (user.kiln_limit != null && user.kiln_limit !== '') {
      data.kiln_limit = user.kiln_limit;
    }
    if (user.kiln_serials && Array.isArray(user.kiln_serials) && user.kiln_serials.length > 0) {
      data.kiln_serials = user.kiln_serials;
    }
    if (user.preferred_language != null && user.preferred_language !== '') {
      data.preferred_language = user.preferred_language;
    }
    if (user.is_school != null) {
      data.is_school = !!user.is_school;
    }
    if (user.school_contact_title != null && user.school_contact_title !== '') {
      data.school_contact_title = user.school_contact_title.trim();
    }
    if (user.school_name != null && user.school_name !== '') {
      data.school_name = user.school_name.trim();
    }
    if (user.school_address != null && user.school_address !== '') {
      data.school_address = user.school_address.trim();
    }

    //createUser:
    return this.http.post(newUserUrl, data, httpOptions).toPromise()
  }

  public createNewKilnLinkPremiumAndCognigotoUser(user): Promise<any> {
    var newUserUrl = `${this.claimUrl}/users/`;
    const httpOptions = {
      headers: this.genericHeaders()
    }
    let data = {
      email: user.email && user.email.trim(),
      password: user.password,
      password_confirmation: user.password
    };

    return this.http.post(newUserUrl, data, httpOptions).toPromise()
  }

  public linkMySkutt(username, email): Promise<any> {
    var emailUpdateUrl = `${this.claimUrl}/users/myskuttlink/${username}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      })
    }

    return new Promise((resolve, reject) => {
      let linkPromise = this.http.put(emailUpdateUrl, {email: email}, httpOptions)
      linkPromise.subscribe(() => {
        this.storageService.set('myskutt_username', undefined);
        resolve({});
      })
    })
  }

  public checkCognitoEmail(email): Promise<any> {
    var emailUpdateUrl = `${this.claimUrl}/users/myskuttlink`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      })
    }
    return this.http.post(emailUpdateUrl, {email: email}, httpOptions).toPromise()
  }

  public headersWithToken() {
    if(this.userLoginData) {
      return {
        headers: new HttpHeaders({
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'x-access-token': this.userLoginData.token
        })
      }
    } else {
      return {
        headers: new HttpHeaders({
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        })
      }
    }
  }

  public isReadonlyUser() {
    let loginData = this.getLoginData();
    let is_logged_in_as = this.isLoggedInAsAnotherUser();
    if(is_logged_in_as) {
      return loginData.loggedInAs && loginData.loggedInAs.user_type_for_account === 'readonly';
    } else {
      return loginData.user_type_for_account ? (loginData.user_type_for_account === 'readonly') : true;
    }
  }

  /** True when the current user is an admin on this KilnLink account (can manage users/kilns in Account Admin). */
  public isAccountAdmin(): boolean {
    const loginData = this.getLoginData();
    if (!loginData) {
      return false;
    }
    if (this.isLoggedInAsAnotherUser()) {
      return loginData.loggedInAs?.user_type_for_account === 'admin';
    }
    return loginData.user_type_for_account === 'admin';
  }

  /** Current user's permission level on the active KilnLink account. */
  public getAccountUserType(): 'admin' | 'basic' | 'readonly' | null {
    const loginData = this.getLoginData();
    if (!loginData) {
      return null;
    }
    const type = this.isLoggedInAsAnotherUser()
      ? loginData.loggedInAs?.user_type_for_account
      : loginData.user_type_for_account;
    if (type === 'admin' || type === 'basic' || type === 'readonly') {
      return type;
    }
    return null;
  }

  /**
   * True when landing/account quick actions (add kiln, add user) should be shown:
   * super-admin, technician (tech), account admin, or kiln owner.
   */
  public canManageAccountQuickActions(): boolean {
    if (this.hasElevatedPermissions() || this.isAccountAdmin()) {
      return true;
    }
    const loginData = this.getLoginData();
    if (!loginData) {
      return false;
    }
    const kilnInfo = this.isLoggedInAsAnotherUser()
      ? (this.loggedInAsUserLoginData?.kiln_info || [])
      : (loginData.kiln_info || []);
    return kilnInfo.some((kiln) => (kiln.role || '').toLowerCase() === 'owner');
  }

  public premiumUser(user, isUpdate = undefined, account_id): Promise<any> {
    let is_logged_in_as = this.isLoggedInAsAnotherUser();
    let loginInfo = this.getLoginData();
    var newUserUrl = `${this.claimUrl}/account/${account_id}/users/premium${is_logged_in_as && loginInfo && loginInfo.loggedInAs ? `?loginAs=${loginInfo.loggedInAs.email || loginInfo.loggedInAs.username}` : ''}`;

    let data = {
      email: user.email && user.email.trim(),
      enabled: user.enabled,
      type: user.type,
      first_name: user.first_name,
      last_name: user.last_name
    };

    if(isUpdate) {
      data['user_id'] = isUpdate.id
      // console.log(isUpdate)
      return this.http.put(newUserUrl, data, this.headersWithToken()).toPromise()
    } else {
      return this.http.post(newUserUrl, data, this.headersWithToken()).toPromise()
    }
  }

  public updatePhone(selected_user, phones): Promise<any> {
    let username = selected_user.username || selected_user.id;
    var phoneUpdateUrl = `${this.claimUrl}/users/premium/${username}/phone`;
    return this.http.post(phoneUpdateUrl, {phones: phones}, this.headersWithToken()).toPromise()
  }

  public updateEmail(selected_user, email): Promise<any> {
    let username = selected_user.username;
    var emailUpdateUrl = `${this.claimUrl}/users/premium/${username}/email`;
    //premiumEmailUpdate:
    return this.http.post(emailUpdateUrl, {email: email}, this.headersWithToken()).toPromise()
  }

  public updateKilnSecurity(username, account_id, data): Promise<any> {
    var emailUpdateUrl = `${this.claimUrl}/premium/account/${account_id}/kilns/access`;
    return this.http.put(emailUpdateUrl, {...{user_id: username}, ...data}, this.headersWithToken()).toPromise()
  }

  public removeUserFromAccount(account_id, email): Promise<any> {
    const accountId = String(account_id ?? '').trim();
    const parsedAccountId = parseInt(accountId, 10);
    if (!Number.isInteger(parsedAccountId) || parsedAccountId <= 0) {
      return Promise.reject({ message: 'Invalid account id' });
    }
    var removeUserUrl = `${this.claimUrl}/account/${parsedAccountId}/users/premium/${email}?email=${email}`;
    return this.http.delete(removeUserUrl, this.headersWithToken())
        .toPromise()
        // .then(response => {
        //   console.log("removed user success: " + user_id)
        //   this.events.publish('premiumUserRemove:success', {...{removed: user_id}, ...response})
        // })
        // .catch(error => this.events.publish('premiumUserRemove:error', error));
  }

  public resetPassword(val): Promise<any> {
    var resetUrl = `${this.claimUrl}/users/password/reset`;
    var httpOptions;
    if(this.userLoginData) {
      httpOptions = this.headersWithToken()
    } else {
      httpOptions = {
        headers: this.genericHeaders()
      }
    }

    return this.http.post(resetUrl, {user: val}, httpOptions).toPromise()
  }

  public updatePassword(val, old_password, email, userAttributes): Promise<any> {
    if(!email) {
      email = this.userLoginData.email;
    }

    var updateUrl = `${this.claimUrl}/users/password/update`;
    const httpOptions = {
      headers: this.genericHeaders()
    }
    const body: Record<string, unknown> = {
      email: email,
      old_password: old_password,
      password: val,
    };
    const sanitizedAttributes = this.sanitizeNewPasswordUserAttributes(userAttributes);
    if (sanitizedAttributes) {
      body.userAttributes = sanitizedAttributes;
    }
    return this.http.post(updateUrl, body, httpOptions).toPromise()
  }

  /** Cognito rejects email and other immutable attrs in the new-password challenge. */
  private sanitizeNewPasswordUserAttributes(
    userAttributes?: Record<string, string>
  ): Record<string, string> | undefined {
    if (!userAttributes || typeof userAttributes !== 'object') {
      return undefined;
    }
    const sanitized = { ...userAttributes };
    ['email', 'email_verified', 'phone_number_verified', 'sub'].forEach((key) => delete sanitized[key]);
    return Object.keys(sanitized).length ? sanitized : undefined;
  }

  public confirmUser(val): Promise<any> {
    var newUserUrl = `${this.claimUrl}/users/confirm`;
    const httpOptions = {
      headers: this.genericHeaders()
    }
    return this.http.post(newUserUrl, val, httpOptions).toPromise()
  }

  public LoggedIn() {
    return this.isLoggenIn;
  }

  // public refreshLogin(): Promise<any> {
//     this.storageService.get('email').then((val) => {
//       const savedEmail = val;
//       this.storageService.get('password').then((pwdVal) => {
//         var loginUrl = this.claimUrl + "/users/login";
//         let data = {
//          "email": savedEmail && savedEmail.trim(),
//          "password": pwdVal
//         }
//
//         const httpOptions = { headers: this.genericHeaders() }
//         this.http.post(loginUrl, data, httpOptions)
//           .toPromise()
//           .then(res => this.events.publish('refeshLogin:success'))
//           .catch(error => this.events.publish('refeshLogin:error'));
//       });
//     })
  // }

  public getLoginData() {
    if(!this.userLoginData || !this.userLoginData.token) {
      this.storageService.get('email').then((val) => {
        const savedEmail = val;
        this.storageService.get('password').then((pwdVal) => {
          const savedPwd = pwdVal;
          this.tryLogin(savedEmail, savedPwd, '');
        })
      });
    } else {
      let loginDta = this.userLoginData;
      if(this.loginAs) {
        loginDta = {...this.userLoginData, ...{loggedInAs: this.loggedInAsUserLoginData}}
      }
      
      return loginDta;
    }
  }

  public isLoggedInAsAnotherUser() {
    return this.loginAs !== undefined;
  }

  public nameAndCompany() {
    let loginData = this.getLoginData();
    // console.log(loginData)
    if(loginData && (loginData.company || loginData.name || (this.loggedInAsUserLoginData && (this.loggedInAsUserLoginData.company || this.loggedInAsUserLoginData.name)))) {
      return {
        name: this.loginAs ? this.loggedInAsUserLoginData.name : loginData.name,
        email: this.loginAs ? (this.loggedInAsUserLoginData.email || this.loggedInAsUserLoginData.username) : loginData.email,
        company: this.loginAs ? this.loggedInAsUserLoginData.company : loginData.company
      }
    } else {
      return loginData ? loginData.email : "email unavailable";
    }
  }

  private mergeImpersonatedKilnInfo(
    incoming?: Array<{ id: string; name?: string; role?: string }>,
  ): void {
    if (!this.loggedInAsUserLoginData) {
      return;
    }

    const byId = new Map<string, { id: string; name?: string; role?: string }>(
      (this.loggedInAsUserLoginData.kiln_info || []).map((kiln) => [kiln.id, {...kiln}]),
    );

    (incoming || []).forEach((kiln) => {
      if (!kiln?.id) {
        return;
      }
      const prior = byId.get(kiln.id) || { id: kiln.id };
      byId.set(kiln.id, {
        ...prior,
        ...kiln,
        name: kiln.name || prior.name,
        role: kiln.role || prior.role,
      });
    });

    this.loggedInAsUserLoginData.kiln_info = Array.from(byId.values());
  }

  public setCurrentAccount(account_id): Promise<any> {
    let loginData = this.getLoginData();
    return new Promise((resolve, reject) => {      
      if(loginData) {        
        try {
          loginData.account = account_id;
          var loginUrl = this.claimUrl + "/premium/admin/login";
          const impersonated = this.isLoggedInAsAnotherUser();
          const email = impersonated
            ? (this.loggedInAsUserLoginData?.email || '').trim()
            : (loginData.email || '').trim();
          const username = impersonated
            ? this.loggedInAsUserLoginData?.username
            : loginData.username;
          let data: any = { account_id: account_id };
          if (email) {
            data.email = email;
          } else if (username) {
            data.username = username;
          } else {
            data.email = loginData.email;
          }
          let setAccountPromise = this.http.post(loginUrl, data, this.headersWithToken())
          setAccountPromise.subscribe(res => {
            if(res && res.hasOwnProperty('role')) {
              delete res["role"];
            }

            if (impersonated) {
              const priorKilnInfo = this.loggedInAsUserLoginData?.kiln_info || [];
              const incomingKilnInfo = (res as { kiln_info?: Array<{ id: string; name?: string; role?: string }> })?.kiln_info;
              this.loggedInAsUserLoginData = {...this.loggedInAsUserLoginData, ...res};
              delete this.loggedInAsUserLoginData.kiln_info;
              this.loggedInAsUserLoginData.kiln_info = [...priorKilnInfo];
              this.mergeImpersonatedKilnInfo(incomingKilnInfo);
              this.loginAsInformation.next(this.loggedInAsUserLoginData);
            } else {
              this.userLoginData = {...this.userLoginData, ...res};
            }
            this.currentAccountUpdate.next(this.isLoggedInAsAnotherUser() ? this.loginAsInformation.getValue() : this.userLoginData)
            this.refreshAccountFilterOptions().finally(() => resolve(this.userLoginData));
          })
        } catch(e) {
          console.log(e)
        }
      } else { reject({}); }      
    })
  }

  private clearAccountCatalogCache(): void {
    this.allKilnInfo = [];
    this.allKilnInfoOwner = null;
    this.accountOptionsForFilter = [];
    this.loadAllKilnInfoRequestId += 1;
  }

  private effectiveCatalogUserKey(): string {
    if (this.isLoggedInAsAnotherUser()) {
      const info = this.loggedInAsUserLoginData;
      return (info?.email || info?.username || '').toLowerCase();
    }
    return (this.userLoginData?.email || this.userLoginData?.username || '').toLowerCase();
  }

  private loginAsQueryString(): string {
    if (!this.isLoggedInAsAnotherUser()) {
      return '';
    }
    const loginInfo = this.loggedInAsUserLoginData;
    const identifier = loginInfo?.email || loginInfo?.username;
    return identifier ? `?loginAs=${encodeURIComponent(identifier)}` : '';
  }

  public refreshAccountFilterOptions(): Promise<Array<{ account_id: number; account_name?: string }>> {
    if (this.isLoggedInAsAnotherUser()) {
      return this.loadAccountsForImpersonatedUserFilter();
    }
    return this.loadAllKilnInfo().then((rows) => {
      this.accountOptionsForFilter = this.distinctAccountsFromRows(rows || []);
      this.accountFilterOptionsUpdate.next();
      return this.accountOptionsForFilter;
    });
  }

  private loadAccountsForImpersonatedUserFilter(): Promise<Array<{ account_id: number; account_name?: string }>> {
    const loginInfo = this.loggedInAsUserLoginData;
    const identifier = loginInfo?.email || loginInfo?.username;
    if (!identifier) {
      this.accountOptionsForFilter = [];
      this.accountFilterOptionsUpdate.next();
      return Promise.resolve([]);
    }

    const url = `${this.claimUrl}/accounts?loginAs=${encodeURIComponent(identifier)}`;
    return this.http.get<any[]>(url, this.headersWithToken()).toPromise()
      .then((rows) => {
        this.accountOptionsForFilter = (Array.isArray(rows) ? rows : []).map((acct) => ({
          account_id: acct.id,
          account_name: acct.name,
        }));
        this.accountFilterOptionsUpdate.next();
        return this.accountOptionsForFilter;
      })
      .catch((err) => {
        console.log('loadAccountsForImpersonatedUserFilter failed', err);
        this.accountOptionsForFilter = [];
        this.accountFilterOptionsUpdate.next();
        return [];
      });
  }

  public loadAllKilnInfo(): Promise<Array<{ id: string; name: string; role: string; account_id: number; account_name?: string }>> {
    if (!this.LoggedIn()) {
      this.allKilnInfo = [];
      this.allKilnInfoOwner = null;
      return Promise.resolve([]);
    }

    if (this.isLoggedInAsAnotherUser()) {
      return this.enrichImpersonatedKilnCatalog();
    }

    const requestId = ++this.loadAllKilnInfoRequestId;
    const owner = this.effectiveCatalogUserKey();
    const url = `${this.claimUrl}/premium/kilns/mine${this.loginAsQueryString()}`;
    return this.http.get<any[]>(url, this.headersWithToken()).toPromise()
      .then((rows) => {
        if (requestId !== this.loadAllKilnInfoRequestId) {
          return this.allKilnInfo;
        }
        this.allKilnInfo = Array.isArray(rows) ? rows : [];
        this.allKilnInfoOwner = owner;
        this.accountOptionsForFilter = this.distinctAccountsFromRows(this.allKilnInfo);
        this.accountFilterOptionsUpdate.next();
        return this.allKilnInfo;
      })
      .catch((err) => {
        console.log('loadAllKilnInfo failed', err);
        if (requestId === this.loadAllKilnInfoRequestId) {
          this.allKilnInfo = [];
          this.allKilnInfoOwner = null;
        }
        return [];
      });
  }

  public getImpersonatedKilnInfo(): Array<{ id: string; name?: string; role?: string }> {
    return this.loggedInAsUserLoginData?.kiln_info || [];
  }

  private getImpersonatedKilnCatalogRows(): Array<{ id: string; name: string; role: string; account_id: number; account_name?: string }> {
    const accountId = parseInt(String(this.loggedInAsUserLoginData?.account ?? ''), 10);
    const accountName = this.loggedInAsUserLoginData?.company;
    return this.getImpersonatedKilnInfo().map((kiln) => ({
      id: kiln.id,
      name: kiln.name || 'Name Unknown',
      role: kiln.role || 'viewer',
      account_id: Number.isFinite(accountId) ? accountId : 0,
      account_name: accountName,
    }));
  }

  private mergeImpersonatedKilnCatalog(
    catalog: Array<{ id: string; name: string; role: string; account_id: number; account_name?: string }>,
  ): void {
    if (!this.loggedInAsUserLoginData) {
      return;
    }

    if (!this.loggedInAsUserLoginData.kiln_info) {
      this.loggedInAsUserLoginData.kiln_info = [];
    }

    catalog.forEach((row) => {
      if (!row?.id) {
        return;
      }

      let kilnInfo = this.loggedInAsUserLoginData.kiln_info.find((kiln) => kiln.id === row.id);
      if (!kilnInfo) {
        kilnInfo = {
          id: row.id,
          name: row.name || 'Name Unknown',
          role: row.role || 'viewer',
        };
        this.loggedInAsUserLoginData.kiln_info.push(kilnInfo);
      } else {
        if (row.name) {
          kilnInfo.name = row.name;
        }
        if (row.role) {
          kilnInfo.role = row.role;
        }
      }
    });
  }

  private enrichImpersonatedKilnCatalog(): Promise<Array<{ id: string; name: string; role: string; account_id: number; account_name?: string }>> {
    const requestId = ++this.loadAllKilnInfoRequestId;
    const fallback = this.getImpersonatedKilnCatalogRows();
    const url = `${this.claimUrl}/premium/kilns/mine${this.loginAsQueryString()}`;

    const applyCatalog = (
      catalog: Array<{ id: string; name: string; role: string; account_id: number; account_name?: string }>,
    ) => {
      this.mergeImpersonatedKilnCatalog(catalog);
      this.allKilnInfo = catalog;
      this.allKilnInfoOwner = this.effectiveCatalogUserKey();
      this.accountOptionsForFilter = this.distinctAccountsFromRows(catalog);
      this.accountFilterOptionsUpdate.next();
      return catalog;
    };

    return this.http.get<any[]>(url, this.headersWithToken()).toPromise()
      .then((rows) => {
        if (requestId !== this.loadAllKilnInfoRequestId) {
          return fallback;
        }

        const catalog = Array.isArray(rows) && rows.length > 0 ? rows : fallback;
        return applyCatalog(catalog);
      })
      .catch(() => applyCatalog(fallback));
  }

  public getCatalogKilnIds(): string[] {
    if (this.isLoggedInAsAnotherUser()) {
      const owner = this.effectiveCatalogUserKey();
      if (this.allKilnInfoOwner === owner && this.allKilnInfo && this.allKilnInfo.length > 0) {
        return this.allKilnInfo.map((kiln) => kiln.id);
      }
      return this.getImpersonatedKilnInfo().map((kiln) => kiln.id).filter(Boolean);
    }

    const owner = this.effectiveCatalogUserKey();
    if (this.allKilnInfoOwner === owner && this.allKilnInfo && this.allKilnInfo.length > 0) {
      return this.allKilnInfo.map((kiln) => kiln.id);
    }

    const loginData = this.getLoginData();
    return loginData?.kiln_info ? loginData.kiln_info.map((kiln) => kiln.id) : [];
  }

  private fetchUserAccounts(): Promise<any[]> {
    const isLoggedInAs = this.isLoggedInAsAnotherUser();
    const loginInfo = this.getLoginData()?.loggedInAs;
    const loginAsQuery =
      isLoggedInAs && loginInfo && (loginInfo.email || loginInfo.username)
        ? `?loginAs=${encodeURIComponent(loginInfo.email || loginInfo.username)}`
        : '';
    const url = `${this.claimUrl}/accounts${loginAsQuery}`;
    return this.http.get<any[]>(url, this.headersWithToken()).toPromise()
      .then((rows) => (Array.isArray(rows) ? rows : []))
      .catch(() => []);
  }

  public getAdminAccounts(): Promise<Array<{ id: number; name: string }>> {
    return this.fetchUserAccounts()
      .then((rows) => rows
        .filter((acct) => acct.user_account_type === 'admin')
        .map((acct) => ({ id: acct.id, name: acct.name }))
      );
  }

  public ownsFreeOrTrialAccount(): Promise<boolean> {
    const loginData = this.getLoginData();
    const email = (
      this.isLoggedInAsAnotherUser()
        ? (loginData?.loggedInAs?.email || loginData?.loggedInAs?.username)
        : loginData?.email
    )?.toString().trim().toLowerCase() || '';
    return this.fetchUserAccounts().then((rows) => {
      if (!email) {
        return false;
      }
      return rows.some((acct) => {
        const type = acct?.type;
        if (type !== 'Free' && type !== 'Trial') {
          return false;
        }
        const ownerEmail = (acct.owner_email || '').trim().toLowerCase();
        if (ownerEmail) {
          return ownerEmail === email;
        }
        return acct.user_account_type === 'admin';
      });
    });
  }

  public getDistinctCatalogAccounts(
    fallbackKilns?: Array<{ account_id?: number; account_name?: string }>,
  ): Array<{ account_id: number; account_name?: string }> {
    if (this.isLoggedInAsAnotherUser()) {
      return this.accountOptionsForFilter;
    }

    const owner = this.effectiveCatalogUserKey();
    const catalog = this.allKilnInfoOwner === owner ? (this.allKilnInfo || []) : [];
    const fromCatalog = this.distinctAccountsFromRows(catalog);
    if (fromCatalog.length >= 2) {
      return fromCatalog;
    }

    if (this.accountOptionsForFilter.length > fromCatalog.length) {
      return this.accountOptionsForFilter;
    }

    const fromKilns = this.distinctAccountsFromRows(
      (fallbackKilns || []).filter((kiln) => kiln?.account_id != null),
    );
    return fromKilns.length > fromCatalog.length ? fromKilns : fromCatalog;
  }

  private distinctAccountsFromRows(
    rows: Array<{ account_id?: number; account_name?: string }>,
  ): Array<{ account_id: number; account_name?: string }> {
    const seen = new Set<number>();
    const accounts: Array<{ account_id: number; account_name?: string }> = [];
    (rows || []).forEach((row) => {
      const accountId = row?.account_id;
      if (accountId == null || seen.has(accountId)) {
        return;
      }
      seen.add(accountId);
      accounts.push({ account_id: accountId, account_name: row.account_name });
    });
    return accounts;
  }

  public shouldShowAccountKilnFilter(
    fallbackKilns?: Array<{ account_id?: number; account_name?: string }>,
  ): boolean {
    return this.getDistinctCatalogAccounts(fallbackKilns).length >= 2;
  }

  public showAccountKilnBadge(): boolean {
    return this.accountKilnFilter === 'all' && this.shouldShowAccountKilnFilter();
  }

  public filterKilnsByAccount(kilns: any[]): any[] {
    if (!Array.isArray(kilns)) {
      return [];
    }
    if (this.accountKilnFilter === 'all') {
      return kilns;
    }
    return kilns.filter((kiln) => kiln.account_id === this.accountKilnFilter);
  }

  public setAccountKilnFilter(value: 'all' | number) {
    this.accountKilnFilter = value;
  }

  public hasKilnlinkFreeKilns() {
    return this.userLoginData ? this.userLoginData.has_kilnlink_free_kilns : false;
  }

  public isInFreeTrial() {
    return true;
    // return this.userLoginData ? this.userLoginData.free_trial : false;
  }

  public genericHeaders() {
    return new HttpHeaders({
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    });
  }

  public updateKilnName(id, name) {
    this.userLoginData.kiln_info = this.userLoginData.kiln_info.map((k) => {
      if(k.id == id) {
        k.name = name;
      }
      return k;
    })
  }

  public HasAcceptedTerms(){
    if (this.userLoginData.needs_to_accept_new_terms_and_conditions !== undefined){
      return !this.userLoginData.needs_to_accept_new_terms_and_conditions;
    } else {
      return false;
    }
  }

  public getAuthToken() {
    return this.userLoginData ? this.userLoginData.token : null;
  }

  public deleteUser(): Promise<any> {
    var updateUrl = `${this.claimUrl}/users/self`;
    let headers = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'x-access-token': this.userLoginData.token,
        'x-arch-token': this.userLoginData.access
      })
    }
    return this.http.delete(updateUrl, headers).toPromise()
  }

  public removeUserFromAlerts(email: string): Promise<any> {
    var updateUrl = `${this.claimUrl}/premium/alerts/remove-user`;
    let headers = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'x-access-token': this.userLoginData.token,
        'x-arch-token': this.userLoginData.access
      })
    }
    return this.http.post(updateUrl, {email: email}, headers).toPromise()
  }

  getBetaUsers(): Promise<any> {
    const headers = new HttpHeaders().set('x-access-token', this.getAuthToken());
    return this.http.get<any>(`${this.claimUrl}/admin/beta-users`, { headers }).toPromise();
  }

  getTechnicians(): Promise<any> {
    const headers = new HttpHeaders().set('x-access-token', this.getAuthToken());
    return this.http.get<any>(`${this.claimUrl}/admin/technicians`, { headers }).toPromise();
  }

  /**
   * Check if a user is a beta user
   */
  checkBetaUserStatus(email: string): Observable<boolean> {
    const headers = new HttpHeaders().set('x-access-token', this.getAuthToken());
    return this.http.get<boolean>(`${this.claimUrl}/admin/beta-users/${encodeURIComponent(email)}`, { headers });
  }

  /**
   * Add a user to beta users list
   */
  addBetaUser(email: string): Promise<any> {
    const headers = new HttpHeaders().set('x-access-token', this.getAuthToken());
    return this.http.post(`${this.claimUrl}/admin/beta-users`, { email }, { headers }).toPromise();
  }

  /**
   * Remove a user from beta users list
   */
  removeBetaUser(email: string): Promise<any> {
    const headers = new HttpHeaders().set('x-access-token', this.getAuthToken());
    return this.http.delete(`${this.claimUrl}/admin/beta-users/${encodeURIComponent(email)}`, { headers }).toPromise();
  }

  private accountPremiumById = new Map<number, boolean>();

  public refreshAccountPremiumCache(): Promise<void> {
    const loginAsQuery = this.isLoggedInAsAnotherUser() ? this.loginAsQueryString() : '';
    const url = `${this.claimUrl}/accounts${loginAsQuery}`;
    return this.http.get<any[]>(url, this.headersWithToken()).toPromise()
      .then((rows) => {
        this.accountPremiumById.clear();
        (rows || []).forEach((acct) => {
          if (acct?.id != null) {
            this.accountPremiumById.set(
              acct.id,
              acct.type !== 'Free' && acct.active !== false,
            );
          }
        });
      })
      .catch(() => {});
  }

  public findCatalogRow(kilnId: string | undefined | null): KilnCatalogRow | undefined {
    if (!kilnId) {
      return undefined;
    }
    return (this.allKilnInfo || []).find((row) => row.id === kilnId);
  }

  /** True when the kiln's account is Premium/Trial and still active. */
  public isCatalogRowPremiumActive(row: KilnCatalogRow | undefined): boolean {
    if (!row) {
      return false;
    }
    if (row.account_type === 'Free') {
      return false;
    }
    if (row.account_type != null) {
      if (row.account_active === false) {
        return false;
      }
      if (row.account_subscription_end_date) {
        return isSubscriptionActive(row.account_subscription_end_date);
      }
      return row.account_type !== 'Free';
    }
    return this.accountPremiumById.get(row.account_id) === true;
  }

  /** True when maintenance notes should be blocked for this kiln (Free or inactive Premium). */
  public isKilnMaintenanceNotesBlocked(kilnId: string | undefined | null): boolean {
    return !this.isCatalogRowPremiumActive(this.findCatalogRow(kilnId));
  }

  public getMaintenanceKilnOptions(): KilnCatalogRow[] {
    return [...(this.allKilnInfo || [])].sort((a, b) => {
      const aPremium = this.isCatalogRowPremiumActive(a);
      const bPremium = this.isCatalogRowPremiumActive(b);
      if (aPremium !== bPremium) {
        return aPremium ? -1 : 1;
      }
      const aLabel = `${a.name || a.id} ${a.account_name || ''}`.toLowerCase();
      const bLabel = `${b.name || b.id} ${b.account_name || ''}`.toLowerCase();
      return aLabel.localeCompare(bLabel);
    });
  }

  public getPreferredMaintenanceKilnId(): string | undefined {
    const options = this.getMaintenanceKilnOptions();
    const premium = options.find((row) => this.isCatalogRowPremiumActive(row));
    return (premium || options[0])?.id;
  }
}
